/**
 * Loader for Google Identity Services (GIS), the script that renders the
 * official "Sign in with Google" button and hands back an ID token.
 *
 * The token it produces is worthless on its own: the API verifies the
 * signature, issuer and audience before it will mint a session. Nothing here
 * is a security boundary, so a blocked or slow script is treated as "Google
 * sign-in is unavailable" rather than an error the user has to act on.
 */

export interface GoogleCredentialResponse {
  credential?: string;
}

export interface GoogleButtonOptions {
  type?: "standard" | "icon";
  theme?: "outline" | "filled_blue" | "filled_black";
  size?: "small" | "medium" | "large";
  text?: "signin_with" | "signup_with" | "continue_with" | "signin";
  shape?: "rectangular" | "pill" | "circle" | "square";
  logo_alignment?: "left" | "center";
  width?: number;
  locale?: string;
}

export interface GoogleAccountsId {
  initialize(config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    use_fedcm_for_prompt?: boolean;
    itp_support?: boolean;
  }): void;
  renderButton(parent: HTMLElement, options: GoogleButtonOptions): void;
  /**
   * One Tap. Under FedCM the browser owns the dialog, so the only thing we can
   * observe is that it was asked for; the outcome arrives through the same
   * credential callback as the button.
   */
  prompt(listener?: (notification: GoogleOneTapNotification) => void): void;
  cancel(): void;
  disableAutoSelect(): void;
}

export interface GoogleOneTapNotification {
  isNotDisplayed?: () => boolean;
  isSkippedMoment?: () => boolean;
  getNotDisplayedReason?: () => string;
  getSkippedReason?: () => string;
}

interface GoogleGlobal {
  accounts?: { id?: GoogleAccountsId };
}

const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const GIS_SCRIPT_ID = "google-identity-services";
const LOAD_TIMEOUT_MS = 10_000;

const getGoogleGlobal = (): GoogleGlobal | undefined =>
  (window as Window & { google?: GoogleGlobal }).google;

/**
 * The OAuth web client ID, injected at build time. Google sign-in is hidden
 * entirely when it is unset, so an environment that has not been configured
 * degrades to the email flow instead of showing a button that cannot work.
 */
export const getGoogleClientId = (): string | undefined => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim();
  return clientId ? clientId : undefined;
};

export const isGoogleSignInConfigured = (): boolean => getGoogleClientId() !== undefined;

/**
 * True only inside an embedded WebView, where Google refuses to run its
 * JavaScript sign-in (the policy behind `disallowed_useragent`) and
 * renderButton silently draws nothing.
 *
 * Deliberately narrow, and deliberately NOT detectWebView() from webViewUtils.
 * That helper exists to nudge layout and errs toward "yes": it treats any UA
 * containing "Version/" as an app, which every mobile Safari does, and any
 * "CriOS" as one, which Chrome on iOS does. Erring that way is harmless for
 * padding and wrong here, where it hid the button from every iPhone browser.
 *
 * So this checks only signals that mean an embedded WebView and nothing else:
 * the object react-native-webview injects (our own app), and the "wv" token
 * Android WebViews put in their UA by convention.
 *
 * The tradeoff is that a third-party in-app browser (Instagram, say) is not
 * detected and may show a button Google then refuses to draw, leaving a gap.
 * That is much the lesser harm: the alternative was no Google sign-in at all
 * for anyone on an iPhone.
 */
export const isEmbeddedWebView = (): boolean => {
  if ((window as Window & { ReactNativeWebView?: unknown }).ReactNativeWebView !== undefined) {
    return true;
  }

  const userAgent = navigator.userAgent || "";
  return /;\s*wv\b/.test(userAgent);
};

let loadPromise: Promise<GoogleAccountsId> | null = null;

/**
 * Resolves with the GIS API once the script is ready. The script is injected
 * at most once per page; concurrent callers share one promise. A failed load
 * clears the promise so a later mount can retry.
 */
export const loadGoogleIdentityServices = (): Promise<GoogleAccountsId> => {
  const ready = getGoogleGlobal()?.accounts?.id;
  if (ready) return Promise.resolve(ready);

  if (loadPromise) return loadPromise;

  loadPromise = new Promise<GoogleAccountsId>((resolve, reject) => {
    let settled = false;

    const finish = () => {
      const api = getGoogleGlobal()?.accounts?.id;
      if (settled) return;
      if (!api) {
        settled = true;
        loadPromise = null;
        reject(new Error("Google Identity Services loaded without an accounts API"));
        return;
      }
      settled = true;
      resolve(api);
    };

    const fail = (reason: string) => {
      if (settled) return;
      settled = true;
      loadPromise = null;
      reject(new Error(reason));
    };

    const timer = window.setTimeout(() => fail("Google Identity Services timed out"), LOAD_TIMEOUT_MS);

    const existing = document.getElementById(GIS_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement("script");

    script.addEventListener("load", () => {
      window.clearTimeout(timer);
      finish();
    });
    script.addEventListener("error", () => {
      window.clearTimeout(timer);
      script.remove();
      fail("Google Identity Services failed to load");
    });

    if (!existing) {
      script.id = GIS_SCRIPT_ID;
      script.src = GIS_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return loadPromise;
};

/**
 * Stops Google from silently re-signing the user in on the next visit. Called
 * on logout: without it, "log out" and "log straight back in" look identical.
 */
export const disableGoogleAutoSelect = (): void => {
  try {
    getGoogleGlobal()?.accounts?.id?.disableAutoSelect();
  } catch {
    // Google not loaded, or blocked, so there is nothing to disable.
  }
};

/** Test seam: forget any in-flight or completed script load. */
export const resetGoogleIdentityLoaderForTests = (): void => {
  loadPromise = null;
};
